<template>
    <div class="calendar-overview-root">
        <Header :label="$t('calendar.header')" overlap="small" />

        <MainLayout marginTop>
            <ProfileModule slot="right" />
            <FeaturedResource slot="right" />
            <VerifyModule slot="right" />
            <WidgetModule slot="right" />
            <div slot="center">
                <Paper class="tab-root mb-3">
                    <Tabs v-model="section" :tabs="tabs()" />
                </Paper>
                <div v-if="profileLoading" slot="center" class="w-100">
                    <Spinner class="loading-spinner mt-5" />
                </div>
                <div v-else-if="!hasCalendar" slot="center">
                    <NoContent :caption="$t('calendar.noCalendar')"></NoContent>
                    <BaseButton
                        id="backButton"
                        class="w-100"
                        variant="primary"
                        style="margin-top:20px;"
                        @click="handleGoBack"
                    >
                        {{ $t('calendar.goBackBtn') }}
                    </BaseButton>
                </div>
                <div
                    v-else-if="!hasCompletedProfile"
                    slot="center"
                    class="w-100 d-flex flex-column align-items-center pb-5"
                >
                    <img
                        :src="profileSvg"
                        style="display:block;margin:auto;width:200px;height:200px;object-fit:scale-down;margin-top:40px;"
                    />
                    <h6 class="text-center mb-4">
                        {{ $t('calendar.completedYourProfile') }}
                    </h6>
                    <BaseButton variant="primary" @click="toProfile">
                        {{ $t('calendar.toMyProfile') }}
                    </BaseButton>
                </div>
                <div v-else slot="center">
                    <div v-if="isLoading()" class="py-2">
                        <ListItemSkeleton v-for="i in 3" :key="i" :rows="3" small />
                    </div>
                    <div class="appointment-grid">
                        <template v-if="isBookSection">
                            <BookAppointmentCard
                                v-for="data in types"
                                :key="data.id"
                                :data="data"
                            />
                        </template>
                        <template v-if="isUpcomingSection">
                            <BookedAppointmentCard
                                v-for="data in upcomingAppointments"
                                :key="data.id"
                                :data="data"
                            />
                        </template>
                        <template v-if="isPastSection">
                            <BookedAppointmentCard
                                v-for="data in pastAppointments"
                                :key="data.id"
                                :data="data"
                            />
                        </template>
                    </div>
                </div>
            </div>
        </MainLayout>
    </div>
</template>

<script>
// Services
import AnalyticService from '~/services/AnalyticService';
import CalendarService from '~/services/CalendarService';

// Components
import Header from '~/components/layouts/Header';
import MainLayout from '~/components/layouts/mainlayout/MainLayout';
import ProfileModule from '~/components/smart/ProfileModule/ProfileModule';
import VerifyModule from '~/components/miscellaneous/VerifyModule';
import FeaturedResource from '~/components/smart/FeaturedResource';
import NoContent from '~/components/miscellaneous/NoContent';

import Paper from '~/components/builders/Paper';

// Assets

import EmptySvg from '~/assets/img/empty.svg';
import BookAppointmentCard from './components/BookAppointmentCard';
import BookedAppointmentCard from './components/BookedAppointmentCard';
import ListItemSkeleton from '~/components/skeletons/ListItemSkeleton';

import URLS from '~/URLS';
import i18n from '~/i18n';
import { maxPossibleScore, getCurrentScore } from '~/utils/mappers/mapProfileToActions';
import ProfileSvg from '~/assets/img/profile.svg';
import WidgetModule from '~/components/smart/WidgetModule.vue';
import MessageService from '~/services/MessageService';

export default {
    name: 'AppointmentList',
    components: {
        ListItemSkeleton,
        BookAppointmentCard,
        BookedAppointmentCard,
        Paper,
        Header,
        MainLayout,
        ProfileModule,
        VerifyModule,
        FeaturedResource,
        NoContent,
        WidgetModule,
    },
    data: () => ({
        emptyImage: EmptySvg,
        profileSvg: ProfileSvg,
        breadcrumb: [
            {
                exact: true,
                text: i18n.t('calendar.bookAnAppointment'),
                to: URLS.calendar.concat(URLS.book),
            },
        ],
        types: [],
        appointments: [],
        loading: true,
        section: 0,
        calendarService: new CalendarService(),
    }),
    computed: {
        hasCompletedProfile() {
            const userData = this.userData() || {};
            const profileData = this.candidateData() || {
                languages: [],
                studyPrograms: [],
            };
            return getCurrentScore(userData, profileData) >= maxPossibleScore;
        },
        profileLoading() {
            return this.loading || this.userLoading() || this.candidateLoading();
        },
        user() {
            return this.$interfaces.user.me.get({
                reactive: true,
            });
        },
        candidate() {
            return this.$interfaces.candidate.me.get({
                reactive: true,
                include: 'summary',
            });
        },
        hasCalendar() {
            return this.types.length > 0;
        },
        isBookSection() {
            return this.section === 0;
        },
        isUpcomingSection() {
            return this.section === 1;
        },
        isPastSection() {
            return this.section === 2;
        },
        upcomingAppointments() {
            const now = new Date();
            return this.appointments.filter(
                appointment =>
                    new Date(appointment.end_time) > now && appointment.status != 'declined'
            );
        },
        pastAppointments() {
            const now = new Date();
            return this.appointments.filter(
                appointment =>
                    new Date(appointment.end_time) < now || appointment.status === 'declined'
            );
        },
    },
    methods: {
        handleBackClick() {
            this.$router.push(this.$URLS.calendar);
        },
        isLoading() {
            return this.loading;
        },
        userLoading() {
            return this.user.loading;
        },
        candidateLoading() {
            return this.candidate.loading;
        },
        userData() {
            return this.user.data;
        },
        candidateData() {
            return this.candidate.data;
        },
        toProfile() {
            this.$router.push(this.$URLS.spotlight);
        },
        handleGoBack() {
            this.$router.back();
        },
        tabs() {
            return [
                {
                    value: 0,
                    label: this.$t('calendar.bookAppointment').concat(' (', this.types.length, ')'),
                },
                {
                    value: 1,
                    label: this.$t('calendar.upcomingAppointments').concat(
                        ' (',
                        this.upcomingAppointments.length,
                        ')'
                    ),
                },
                {
                    value: 2,
                    label: 'Past appointments'.concat(' (', this.pastAppointments.length, ')'),
                },
            ];
        },
        selectTabFromQuery() {
            const query = this.$route.query;
            if (query.section) {
                this.section = Number.parseInt(query.section) | 0;
            }
        },
        refreshData() {
            this.loading = true;
            this.calendarService
                .getAppointmentTypesAndBookings()
                .then(({ types, bookings }) => {
                    this.types = types;
                    this.appointments = bookings;
                })
                .catch(() => {
                    MessageService.error(
                        this.$t('calendar.listAppointments.error.title'),
                        this.$t('calendar.listAppointments.error.description')
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
    async mounted() {
        AnalyticService.impressionEvent(AnalyticService.IMPRESSION_ACTIONS.MYCALENDAR_VIEW_ACTION);

        this.selectTabFromQuery();

        this.calendarService.addListener(event => {
            if (event.detail.action === 'cancel') {
                this.refreshData();
            }
        });

        this.refreshData();
    },
    beforeDestroy() {
        this.calendarService.removeAllListeners();
    },
};
</script>

<style scoped>
.appointment-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: minmax(100px, auto);
    grid-gap: 0.5em;
}

@media only screen and (max-width: 700px) {
    .appointment-grid {
        grid-template-columns: 1fr;
    }
}
</style>
